<template>
  <div class="min-h-screen bg-ds-bckgnd-color p-4 sm:p-8">
    <div
      class="mx-auto max-w-7xl overflow-hidden rounded-2xl bg-white shadow-2xl"
    >
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 bg-header-bg-color px-4 py-4 sm:px-6 sm:py-5"
      >
        <div>
          <h2 class="text-xl sm:text-2xl font-bold tracking-tight text-white">
            IWC Agent Management System
          </h2>
          <p class="text-xs sm:text-sm text-white">
            Manage and view agent details
          </p>
        </div>
        <router-link
          v-if="userRole !== 'Broker'"
          to="/agents/register"
          class="rounded-xl bg-ds-button-color px-5 py-2 font-semibold text-white shadow-md transition hover:scale-105 hover:bg-ds-button-color active:scale-95 text-center"
        >
          + Add Agent
        </router-link>
      </div>

      <div
        class="flex gap-3 border-b border-gray-200 bg-gray-50 px-4 sm:px-6 py-3"
      >
        <button
          v-for="view in filteredViews"
          :key="view.name"
          @click="currentView = view.component"
          :class="[
            'rounded-xl px-4 py-2 font-semibold transition relative',
            currentView === view.component
              ? 'bg-ds-button-color text-white'
              : 'text-gray-700 hover:bg-ds-button-color hover:text-white',
          ]"
        >
          {{ view.labels[userRole] || view.labels.default }}
          <span
            v-if="view.name === 'pending' && pendingCount > 0"
            class="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5"
          >
            {{ pendingCount }}
          </span>
        </button>
      </div>

      <div>
        <component :is="currentView" />
      </div>

      <div style="display: none">
        <PendingApproval @update-count="updatePendingCount" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, watch } from "vue";
import AgentTable from "@/pages/dashboard-nav/AgentTable.vue";
import GenerateBrokerCode from "@/pages/dashboard-nav/GenerateBrokerCode.vue";
import PendingApproval from "@/pages/dashboard-nav/PendingApproval.vue";
import PendingPrinting from "@/pages/dashboard-nav/PendingPrinting.vue";
import AgentQRCode from "@/pages/dashboard-nav/AgentQRCode.vue";
import IDCardPrinting from "@/pages/dashboard-nav/IDCardPrinting.vue";

export default {
  components: {
    AgentTable,
    GenerateBrokerCode,
    PendingApproval,
    PendingPrinting,
    AgentQRCode,
    IDCardPrinting,
  },
  setup() {
    const store = inject("$store");
    const userRole = computed(() => store.state.adminLogDetails.role);
    const name = computed(() => store.state.adminLogDetails.name);

    const allViews = [
      {
        name: "dashboard",
        labels: {
          default: "Dashboard",
          staff: "All IWC Agents",
          Broker: "Dashboard",
          super: "All IWC Agents",
        },
        component: "AgentTable",
      },
      {
        name: "generate",
        labels: {
          default: "Generate Code",
          staff: "Generate Code",
          Broker: "Generate Code",
          super: "Generate Code",
        },
        component: "GenerateBrokerCode",
      },
      {
        name: "pending",
        labels: {
          default: "Pending Approval",
          staff: "Approved Applications",
          Broker: "Pending Approval",
          super: "Pending Approval",
        },
        component: "PendingApproval",
      },
      {
        name: "qr",
        labels: {
          default: "Broker QR Code",
          staff: "Broker QR Code",
          Broker: "My QR Code",
          super: "Broker QR Code",
        },
        component: "AgentQRCode",
      },
      {
        name: "id",
        labels: {
          default: "Agent ID Card",
          staff: "Agent ID Card",
          Broker: "My ID Card",
          super: "Agent ID Card",
        },
        component: "IDCardPrinting",
      },
    ];

    const filteredViews = ref([]);

    const setFilteredViews = () => {
      if (userRole.value === "Broker") {
        filteredViews.value = allViews.filter(
          (v) => v.name === "generate" || v.name === "qr",
        );
      } else if (userRole.value === "staff") {
        filteredViews.value = allViews.filter(
          (v) => v.name === "pending" || v.name === "dashboard",
        );
      } else if (userRole.value === "super") {
        filteredViews.value = allViews.filter((v) => v.name !== "id");
      } else {
        filteredViews.value = allViews;
      }
    };

    setFilteredViews();

    const currentView = ref(
      userRole.value === "Broker"
        ? "GenerateBrokerCode"
        : userRole.value === "staff"
        ? "PendingApproval"
        : "AgentTable",
    );

    const pendingCount = ref(0);
    const updatePendingCount = (count) => {
      pendingCount.value += count;
    };

    watch(userRole, setFilteredViews, { immediate: true });

    return {
      filteredViews,
      currentView,
      pendingCount,
      updatePendingCount,
      userRole,
      name,
    };
  },
};
</script>
